import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { Database } from "@/integrations/supabase/types";
import type { PrescriptionTemplateInput } from "@/lib/validations/prescription-template";
import i18n from "@/i18n";
import type { PosologyMode } from "@/lib/prescriptions/posology";
import { normalizeAdministrationRouteValue } from "@/lib/prescriptions/prescription-fields";

type PrescriptionTemplateRow = Database["public"]["Tables"]["prescription_templates"]["Row"];
type LegacyPrescriptionTemplateItemRow = Database["public"]["Tables"]["prescription_template_items"]["Row"];
type ClinicScopedPrescriptionTemplateRow = Omit<PrescriptionTemplateRow, "clinic_id"> & {
  clinic_id: string;
  owner_user_id: string;
};

type PrescriptionTemplateItemRow = Omit<
  LegacyPrescriptionTemplateItemRow,
  "dosage" | "frequency" | "duration" | "instructions"
> & {
  posology_mode: PosologyMode | null;
  free_text_posology: string | null;
  dose_amount: string | null;
  dose_unit: string | null;
  frequency_value: string | null;
  frequency_unit: string | null;
  duration_value: string | null;
  duration_unit: string | null;
};

export type PrescriptionTemplateWithItems = ClinicScopedPrescriptionTemplateRow & {
  items: PrescriptionTemplateItemRow[];
};

export const usePrescriptionTemplates = (
  clinicId?: string | null,
  templateType?: "standard" | "manipulated",
) => {
  return useQuery({
    queryKey: ["prescription-templates", clinicId, templateType ?? "all"],
    enabled: Boolean(clinicId),
    queryFn: async () => {
      if (!clinicId) return [] as PrescriptionTemplateWithItems[];

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let query = (supabase as any)
        .from("prescription_templates")
        .select("*, items:prescription_template_items(*)")
        .eq("clinic_id", clinicId)
        .order("title", { ascending: true });

      if (templateType) {
        query = query.eq("template_type", templateType);
      }

      const { data, error } = await query;

      if (error) throw error;
      return (data ?? []) as PrescriptionTemplateWithItems[];
    },
  });
};

export const useCreatePrescriptionTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: PrescriptionTemplateInput & { clinic_id: string }) => {
      const { data: auth, error: authError } = await supabase.auth.getUser();
      if (authError) throw authError;
      const userId = auth.user?.id ?? null;
      if (!userId) throw new Error(i18n.t("prescriptions:templates.errors.notAuthenticated"));
      const clinicId = payload.clinic_id?.trim();
      if (!clinicId) throw new Error(i18n.t("prescriptions:templates.feedback.createError"));
      const templateType = payload.template_type ?? "standard";

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: template, error } = await (supabase as any)
        .from("prescription_templates")
        .insert({
          clinic_id: clinicId,
          title: payload.title.trim(),
          observations: payload.observations?.trim() || null,
          created_by: userId,
          owner_user_id: userId,
          template_type: templateType,
          manipulated_payload:
            templateType === "manipulated" ? payload.manipulated_payload ?? null : null,
        })
        .select()
        .single();

      if (error) throw error;

      if (templateType !== "manipulated" && (payload.items?.length ?? 0) > 0) {
        const itemInserts: Database["public"]["Tables"]["prescription_template_items"]["Insert"][] =
          payload.items.map((item) => ({
            template_id: template.id,
            medication_id: item.medication_id && item.medication_id !== "" ? item.medication_id : null,
            medication_name: item.medication_name,
            medication_concentration: item.medication_concentration || null,
            pharmaceutical_form: item.pharmaceutical_form || null,
            administration_route: normalizeAdministrationRouteValue(item.administration_route),
            presentation: item.presentation || null,
            posology_mode: item.posology_mode,
            free_text_posology: item.free_text_posology || null,
            dose_amount: item.dose_amount || null,
            dose_unit: item.dose_unit || null,
            frequency_value: item.frequency_value || null,
            frequency_unit: item.frequency_unit || null,
            duration_value: item.duration_value || null,
            duration_unit: item.duration_unit || null,
          }));

        const { error: itemsError } = await supabase
          .from("prescription_template_items")
          .insert(itemInserts);

        if (itemsError) throw itemsError;
      }

      return template;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["prescription-templates", variables.clinic_id], refetchType: "all" });
      toast.success(i18n.t("prescriptions:templates.feedback.created"));
    },
    onError: (error) => {
      console.error("Erro ao criar template de receita:", error);
      toast.error(i18n.t("prescriptions:templates.feedback.createError"));
    },
  });
};

export const useUpdatePrescriptionTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      templateId,
      clinicId,
      payload,
    }: {
      templateId: string;
      clinicId: string;
      payload: PrescriptionTemplateInput;
    }) => {
      if (!clinicId) throw new Error(i18n.t("prescriptions:templates.feedback.updateError"));

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase as any)
        .from("prescription_templates")
        .update({
          title: payload.title.trim(),
          observations: payload.observations?.trim() || null,
          template_type: payload.template_type ?? "standard",
          manipulated_payload:
            payload.template_type === "manipulated" ? payload.manipulated_payload ?? null : null,
        })
        .eq("id", templateId)
        .eq("clinic_id", clinicId);

      if (error) throw error;

      const { error: deleteError } = await supabase
        .from("prescription_template_items")
        .delete()
        .eq("template_id", templateId);

      if (deleteError) throw deleteError;

      if (payload.template_type !== "manipulated" && (payload.items?.length ?? 0) > 0) {
        const itemInserts: Database["public"]["Tables"]["prescription_template_items"]["Insert"][] =
          payload.items.map((item) => ({
            template_id: templateId,
            medication_id: item.medication_id && item.medication_id !== "" ? item.medication_id : null,
            medication_name: item.medication_name,
            medication_concentration: item.medication_concentration || null,
            pharmaceutical_form: item.pharmaceutical_form || null,
            administration_route: normalizeAdministrationRouteValue(item.administration_route),
            presentation: item.presentation || null,
            posology_mode: item.posology_mode,
            free_text_posology: item.free_text_posology || null,
            dose_amount: item.dose_amount || null,
            dose_unit: item.dose_unit || null,
            frequency_value: item.frequency_value || null,
            frequency_unit: item.frequency_unit || null,
            duration_value: item.duration_value || null,
            duration_unit: item.duration_unit || null,
          }));

        const { error: itemsError } = await supabase
          .from("prescription_template_items")
          .insert(itemInserts);

        if (itemsError) throw itemsError;
      }

      return templateId;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["prescription-templates", variables.clinicId], refetchType: "all" });
      toast.success(i18n.t("prescriptions:templates.feedback.updated"));
    },
    onError: (error) => {
      console.error("Erro ao atualizar template de receita:", error);
      toast.error(i18n.t("prescriptions:templates.feedback.updateError"));
    },
  });
};

export const useDeletePrescriptionTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ templateId, clinicId }: { templateId: string; clinicId: string }) => {
      if (!clinicId) throw new Error(i18n.t("prescriptions:templates.feedback.deleteError"));

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase as any)
        .from("prescription_templates")
        .delete()
        .eq("id", templateId)
        .eq("clinic_id", clinicId);
      if (error) throw error;
      return templateId;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["prescription-templates", variables.clinicId], refetchType: "all" });
      toast.success(i18n.t("prescriptions:templates.feedback.deleted"));
    },
    onError: (error) => {
      console.error("Erro ao remover template de receita:", error);
      toast.error(i18n.t("prescriptions:templates.feedback.deleteError"));
    },
  });
};
